/**
 * Utility functions for feedback integration that ensure non-disruptive behavior
 * Implements requirements 2.4, 2.5 - ensuring feedback doesn't disrupt annotation workflow
 */

/**
 * Debounce function to prevent rapid feedback submissions
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Check if feedback submission should be allowed based on current workflow state
 */
export function canSubmitFeedback(): boolean {
  // Check if there are any active form submissions
  const activeSubmissions = document.querySelectorAll('[data-submitting="true"]');
  if (activeSubmissions.length > 0) {
    return false;
  }

  // Check if there are any active navigation events
  const navigationInProgress = document.querySelector('[data-navigation-loading="true"]');
  if (navigationInProgress) {
    return false;
  }

  return true;
}

/**
 * Queue feedback submission to avoid conflicts with other operations
 */
export class FeedbackQueue {
  private static queue: Array<() => Promise<void>> = [];
  private static processing = false;

  static async add(submission: () => Promise<void>): Promise<void> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          await submission();
          resolve();
        } catch (error) {
          reject(error);
        }
      });

      this.processQueue();
    });
  }

  private static async processQueue(): Promise<void> {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      const submission = this.queue.shift();
      if (submission) {
        try {
          await submission();
        } catch (error) {
          console.error('Queued feedback submission failed:', error);
        }
      }
    }

    this.processing = false;
  }
}

/**
 * Prevent feedback popover from interfering with form focus
 */
export function handleFeedbackFocus(event: FocusEvent): void {
  const target = event.target as HTMLElement;
  
  // If focus is moving to a form element, don't interfere
  if (target && (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.tagName === 'BUTTON' ||
    target.closest('form')
  )) {
    // Allow the focus to proceed naturally
    return;
  }
}

/**
 * Check if current context allows feedback submission without disruption
 */
export function isContextSafeForFeedback(): boolean {
  // Check if user is in the middle of audio playback
  const audioElements = document.querySelectorAll('audio');
  const isAudioPlaying = Array.from(audioElements).some(audio => !audio.paused);
  
  // Allow feedback even during audio playback as it shouldn't disrupt it
  // Just log for debugging
  if (isAudioPlaying) {
    console.debug('Feedback submitted during audio playback - this is allowed');
  }

  // Check if there are any active modals or dialogs that might conflict
  const activeModals = document.querySelectorAll('[role="dialog"][aria-hidden="false"]');
  if (activeModals.length > 1) { // More than 1 because feedback popover itself is a modal
    return false;
  }

  return true;
}

/**
 * Ensure feedback button doesn't interfere with keyboard navigation
 */
export function handleFeedbackKeyboard(event: KeyboardEvent): boolean {
  // Don't interfere with form navigation keys
  if (event.key === 'Tab' || event.key === 'Enter' || event.key === 'Space') {
    const target = event.target as HTMLElement;
    
    // If focus is on a form element, let it handle the key
    if (target && (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.closest('form')
    )) {
      return false; // Don't handle the key
    }
  }

  return true; // Allow feedback to handle the key
}